import { motion } from 'framer-motion';

/**
 * Reusable empty-state block — pairs a hand-drawn SVG illustration
 * with a title, optional description, and an optional CTA button slot.
 *
 * Props:
 *   - illustration: ReactNode (an SVG illustration component)
 *   - title: string
 *   - description?: string | ReactNode
 *   - cta?: ReactNode (a button or link)
 *   - tone?: 'default' | 'success' (success uses positive halo)
 *   - size?: 'sm' | 'md' | 'lg'  controls illustration scale
 *   - card?: boolean (default true) — wraps content in a Card
 *   - testId?: string
 */
export default function EmptyState({
  illustration: Illustration,
  title,
  description,
  cta,
  tone = 'default',
  size = 'md',
  card = true,
  testId,
  className = '',
}) {
  const illusSize = size === 'lg' ? 'w-44 h-44' : size === 'sm' ? 'w-24 h-24' : 'w-32 h-32';
  // iter95 Premium Void — generous vertical space, soft surface, dashed border
  // gives every empty state the same calm, intentional feel.
  const padding = size === 'lg' ? 'py-12 px-6' : size === 'sm' ? 'py-6 px-4' : 'py-10 px-5';

  const inner = (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`flex flex-col items-center text-center ${className}`}
      data-testid={testId}
    >
      {Illustration && (
        <div className="mb-4 relative opacity-90">
          <Illustration className={illusSize} />
        </div>
      )}
      <h3 className={`font-display font-bold tracking-tight ${size === 'sm' ? 'text-base' : 'text-xl'}`}>
        {title}
      </h3>
      {description && (
        <p className={`text-muted-foreground mt-2 max-w-[34ch] leading-relaxed ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
          {description}
        </p>
      )}
      {cta && <div className="mt-5">{cta}</div>}
    </motion.div>
  );

  if (!card) return inner;

  return (
    <div
      className={`${padding} rounded-3xl border-2 border-dashed flex items-center justify-center ${
        tone === 'success'
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-border bg-muted/30'
      }`}
    >
      {inner}
    </div>
  );
}
